import React, { useState } from 'react';
import {
  loginAPICall,
  saveLoggedInUser,
  storeToken,
  storeRole,
} from '../../AuthService';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../AuthContext';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const Form = (): JSX.Element => {
  const navigator = useNavigate();
  const { setRole } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState('');

  const validationSchema = yup.object({
    email: yup
      .string()
      .trim()
      .email('Please enter a valid email address')
      .required('Email is required.'),
    password: yup
      .string()
      .required('Please specify your password')
      .min(4, 'The password should have at minimum length of 4'),
  });

  const initialValues = {
    email: '',
    password: '',
  };

  const onSubmit = async (values) => {
    try {
      const response = await loginAPICall(values);
      const token = 'Bearer ' + response.data.jwtToken;
      const role = response.data.role;
      storeToken(token);
      storeRole(role);
      saveLoggedInUser(values.email);
      setRole(role);

      switch (role) {
        case 'SYSTEM':
          navigator('/system/dashboard/sections');
          break;
        case 'ADMIN':
          navigator('/admin/dashboard');
          break;
        case 'MANAGER':
          navigator('/manager/dashboard');
          break;
        case 'USER':
          navigator('/user/home');
          break;
        default:
          navigator('/login');
          break;
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setErrorMessage(
          error.response.data?.message ??
            'Incorrect email or password. Please try again.',
        );
      } else if (!error?.response) {
        setErrorMessage(
          'Unable to connect. Please check your internet connection and try again.',
        );
      } else {
        setErrorMessage('Something went wrong. Please try again later.');
      }
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{
            textTransform: 'uppercase',
            fontWeight: 'medium',
          }}
          gutterBottom
          color={'text.secondary'}
        >
          Login
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
          }}
        >
          Welcome back
        </Typography>
        <Typography color="text.secondary">
          Login to manage your account.
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Enter your email
            </Typography>
            <TextField
              label="Email *"
              variant="outlined"
              name={'email'}
              fullWidth
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              // @ts-ignore
              helperText={formik.touched.email && formik.errors.email}
              InputLabelProps={{
                shrink: true, // Ensures the label floats when there is a value
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretched', sm: 'center' }}
              justifyContent={'space-between'}
              width={1}
              marginBottom={2}
            >
              <Box marginBottom={{ xs: 1, sm: 0 }}>
                <Typography variant={'subtitle2'}>
                  Enter your password
                </Typography>
              </Box>
              <Typography variant={'subtitle2'}>
                <Link
                  component={'a'}
                  color={'primary'}
                  href={'/passwordReset'}
                  underline={'none'}
                >
                  Forgot your password?
                </Link>
              </Typography>
            </Box>
            <TextField
              label="Password *"
              variant="outlined"
              name={'password'}
              type={'password'}
              fullWidth
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              // @ts-ignore
              helperText={formik.touched.password && formik.errors.password}
              InputLabelProps={{
                shrink: true, // Ensures the label floats when there is a value
              }}
            />
            {errorMessage && (
              <Typography color="error" variant="body2" gutterBottom>
                {errorMessage}
              </Typography>
            )}
          </Grid>
          <Grid item container xs={12}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretched', sm: 'center' }}
              justifyContent={'space-between'}
              width={1}
              maxWidth={600}
              margin={'0 auto'}
            >
              <Box marginBottom={{ xs: 1, sm: 0 }}>
                <Typography variant={'subtitle2'}>
                  Don't have an account yet?{' '}
                  <Link
                    component={'a'}
                    color={'primary'}
                    href={'/register'}
                    underline={'none'}
                  >
                    Sign up here.
                  </Link>
                </Typography>
              </Box>
              <Button size={'large'} variant={'contained'} type={'submit'}>
                Login
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default Form;
